import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { RadioButton } from 'primereact/radiobutton';
import axiosInstance from '../config/axios.config';

// ─────────────────────────────────────────────────────────────────────────────
// Icon / label helpers (pure, module-scope — don't recreate on render)
// ─────────────────────────────────────────────────────────────────────────────

const getCategoryIcon = (categoryName = '') => {
    const name = categoryName.toLowerCase();
    if (name.includes('identity') || name.includes('verification')) return 'pi-user-edit';
    if (name.includes('financial') || name.includes('credit')) return 'pi-chart-line';
    if (name.includes('employment')) return 'pi-briefcase';
    if (name.includes('vehicle')) return 'pi-car';
    if (name.includes('professional')) return 'pi-verified';
    return 'pi-file';
};

const getServiceIcon = (serviceName = '') => {
    const name = serviceName.toLowerCase();
    if (name.includes('equifax')) return 'pi-chart-line';
    if (name.includes('bank')) return 'pi-building';
    if (name.includes('pan')) return 'pi-credit-card';
    if (name.includes('voter')) return 'pi-check-square';
    if (name.includes('driving')) return 'pi-car';
    if (name.includes('mobile')) return 'pi-phone';
    if (name.includes('ca') || name.includes('membership')) return 'pi-verified';
    if (name.includes('identity') || name.includes('verification')) return 'pi-user-edit';
    if (name.includes('financial') || name.includes('credit')) return 'pi-chart-line';
    if (name.includes('employment')) return 'pi-briefcase';
    return 'pi-file';
};

const formatINR = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const PlanCard = ({ plan, selected, onSelect }) => {
    const inputId = `plan-${plan.id}`;
    return (
        <div className="col-12 md:col-6 lg:col-3">
            <Card>
                <div className="flex align-items-start gap-2">
                    <RadioButton
                        inputId={inputId}
                        checked={selected}
                        onChange={() => onSelect(plan)}
                    />
                    <label htmlFor={inputId} className="flex flex-column flex-1 min-w-0 cursor-pointer">
                        <span className="text-900 text-base font-semibold">{plan.credits} Credits</span>
                        <span className="text-primary text-lg font-bold">{formatINR(plan.amount)}</span>
                        <span className="text-500 text-xs mt-1">
                            {formatINR((parseFloat(plan.amount) / plan.credits).toFixed(2))} / credit
                        </span>
                        {plan.description && (
                            <span className="text-500 text-xs mt-1">{plan.description}</span>
                        )}
                    </label>
                </div>
            </Card>
        </div>
    );
};

// Mobile card for the Services table — same pattern as LendingNetwork MemberHeader
const ServiceMobileRow = ({ service }) => (
    <div className="flex justify-content-between align-items-center gap-2 w-full">
        <div className="flex align-items-center gap-2 flex-1 min-w-0">
            <div className="flex flex-column min-w-0">
                <span className="font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {service.name}
                </span>
                <span className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {service.category}
                </span>
            </div>
        </div>
        <Tag value={`${service.credits_required || 'N/A'} cr`} severity="info" className="text-xs flex-shrink-0" />
    </div>
);


// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const CreditWallet = () => {
    const toast = useRef(null);

    // Auth user (needed for payment initiation)
    const userData = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
    const userId = userData.id;

    // Wallet + plans
    const [walletBalance, setWalletBalance] = useState(0);
    const [rechargePlans, setRechargePlans] = useState([]);
    const [loading, setLoading] = useState(true);

    // Services
    const [menuServices, setMenuServices] = useState(null);
    const [loadingServices, setLoadingServices] = useState(true);

    // Recharge dialog
    const [rechargeDialog, setRechargeDialog] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [rechargeSubmitted, setRechargeSubmitted] = useState(false);
    const autoOpenedRef = useRef(false);

    // Mobile accordion for the services table (P17). Transaction history now
    // lives on its own page (TransactionHistory.js).
    const [mobileSvcIndex, setMobileSvcIndex] = useState(null);

    // ─────────────────────────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────────────────────────

    const loadWalletData = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/credit-recharge/plans-balance');
            if (response.data.success) {
                const balance = response.data.data.current_balance || 0;
                setWalletBalance(balance);
                setRechargePlans(response.data.data.plans || []);

                // Auto-open recharge dialog exactly once if balance is 0
                if (balance === 0 && !autoOpenedRef.current) {
                    autoOpenedRef.current = true;
                    setRechargeDialog(true);
                }
            }
        } catch (error) {
            console.error('Failed to load wallet data:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load wallet data',
                life: 3000,
            });
        }
    }, []);

    const fetchMenuServices = useCallback(async () => {
        setLoadingServices(true);
        try {
            const response = await axiosInstance.get('/user/report-services/categorized');
            if (response.data?.success && response.data.data) {
                setMenuServices(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch menu services:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load services',
                life: 3000,
            });
        } finally {
            setLoadingServices(false);
        }
    }, []);

    // Initial load — wallet + services in parallel
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            await Promise.all([loadWalletData(), fetchMenuServices()]);
            if (!cancelled) setLoading(false);
        };
        init();
        return () => { cancelled = true; };
    }, [loadWalletData, fetchMenuServices]);

    // ─────────────────────────────────────────────────────────────────────────
    // Handlers
    // ─────────────────────────────────────────────────────────────────────────

    // Initiate Razorpay cross-tenant payment redirect (preserved 1:1)
    const initializePayment = async () => {
        setRechargeSubmitted(true);
        if (!selectedPlan) {
            toast.current?.show({
                severity: 'error',
                summary: 'Select a plan',
                detail: 'Please select a recharge plan',
                life: 3000,
            });
            return;
        }

        setProcessingPayment(true);
        try {
            const response = await axiosInstance.post('/credit-recharge/initiate', {
                recharge_price_id: selectedPlan.id,
                user_id: userId,
            });

            if (response.data?.company_name) {
                const tokenResponse = await axiosInstance.post('/credit-recharge/generate-token', {
                    order_id: response.data.order_id,
                    source_domain: window.location.hostname,
                    source_path: window.location.pathname,
                    payment_confirm_token: response.data.payment_confirm_token,
                });

                if (tokenResponse.data.success) {
                    setRechargeDialog(false);
                    window.location.href = tokenResponse.data.payment_url;
                } else {
                    throw new Error('Payment initialization failed');
                }
            } else {
                throw new Error('Company information missing');
            }
        } catch (error) {
            console.error('Failed to initiate payment:', error?.message || 'Unknown error');
            let message = 'Error in initiating payment. Please try again.';
            if (error.response?.status === 401) message = 'Your session has expired. Please login again.';
            else if (error.response?.status === 403) message = 'You do not have permission to perform this action.';
            else if (error.response?.status === 404) message = 'The selected plan is no longer available. Please refresh and try again.';
            else if (error.response?.status >= 500) message = 'Service temporarily unavailable. Please try again after sometime.';
            else if (error.request && !error.response) message = 'Connection error. Please check your internet and try again.';

            toast.current?.show({
                severity: 'error',
                summary: 'Payment failed',
                detail: message,
                life: 5000,
            });
        } finally {
            setProcessingPayment(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Derived data
    // ─────────────────────────────────────────────────────────────────────────

    // Flatten categorized services object → array of services with category info
    const allServices = useMemo(() => {
        if (!menuServices) return [];
        return Object.values(menuServices).flatMap((category) =>
            (category.services || []).map((service) => ({
                ...service,
                category: category.name,
            })),
        );
    }, [menuServices]);

    // ─────────────────────────────────────────────────────────────────────────
    // Column templates — Services table (desktop)
    // ─────────────────────────────────────────────────────────────────────────

    const categoryBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className={`pi ${getCategoryIcon(rowData.category)} text-primary`} aria-hidden="true" />
            <span className="text-sm text-700">{rowData.category}</span>
        </div>
    );

    const serviceNameBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className={`pi ${getServiceIcon(rowData.name)} text-primary`} aria-hidden="true" />
            <span className="text-sm text-900 font-medium">{rowData.name}</span>
        </div>
    );

    const creditsBodyTemplate = (rowData) => (
        <span className="text-sm font-semibold text-primary">
            {rowData.credits_required || rowData.credits || 'N/A'}
        </span>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <>
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                    <div>
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Credit Wallet</h1>
                        <p className="text-600 text-sm m-0">Manage credits, pricing, and transaction history</p>
                    </div>
                </div>
                <Card>
                    <Skeleton height="8rem" className="mb-3" />
                    <Skeleton height="1.5rem" className="mb-2" />
                    <Skeleton height="1.5rem" />
                </Card>
            </>
        );
    }

    return (
        <>
            <Toast ref={toast} />

            {/* Page header (P6) */}
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Credit Wallet</h1>
                    <p className="text-600 text-sm m-0">
                        Manage your credit balance, view pricing, and track transactions
                    </p>
                </div>
            </div>

            {/* Balance hero card */}
            <Card className="mb-3 shadow-1">
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                    <div className="flex align-items-center gap-3">
                        <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-3 flex-shrink-0">
                            <i className="pi pi-wallet text-primary pi-2x" aria-hidden="true" />
                        </div>
                        <div className="flex flex-column">
                            <span className="text-500 text-xs font-medium">CURRENT BALANCE</span>
                            <div className="flex align-items-baseline gap-2">
                                <span className="text-900 text-xl font-semibold">
                                    {walletBalance.toLocaleString('en-IN')}
                                </span>
                                <span className="text-600 text-sm">Credits</span>
                            </div>
                            <span className="text-500 text-xs mt-1">
                                {walletBalance === 0
                                    ? 'No credits available — recharge to continue.'
                                    : walletBalance < 50
                                        ? 'Running low — consider recharging soon.'
                                        : 'Sufficient balance for most services.'}
                            </span>
                        </div>
                    </div>
                    <Button
                        label="Recharge Wallet"
                        icon="pi pi-plus-circle"
                        size="small"
                        onClick={() => setRechargeDialog(true)}
                        className="md:w-auto w-full"
                    />
                </div>
            </Card>

            {/* Service Credits & Pricing Guide */}
            <Card className="mb-3">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                    <div>
                        <h2 className="text-base font-medium text-900 m-0 mb-1">Service Credits &amp; Pricing Guide</h2>
                        <p className="text-600 text-xs m-0">
                            Credits are consumed per API call. Recharge your wallet to access verification services.
                        </p>
                    </div>
                    <Tag
                        value={`${allServices.length} services`}
                        severity="info"
                        icon="pi pi-th-large"
                        className="flex-shrink-0"
                    />
                </div>

                {loadingServices ? (
                    <div className="flex align-items-center justify-content-center py-5">
                        <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                    </div>
                ) : allServices.length === 0 ? (
                    <div className="flex flex-column align-items-center py-5">
                        <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                        <span className="text-600">No services available</span>
                    </div>
                ) : (
                    <>
                        {/* Mobile (< md) — Accordion cards (P17) */}
                        <div className="block md:hidden">
                            <Accordion
                                activeIndex={mobileSvcIndex}
                                onTabChange={(e) => setMobileSvcIndex(e.index)}
                            >
                                {allServices.map((s, i) => (
                                    <AccordionTab key={`${s.category}-${s.id || s.name}-${i}`} header={<ServiceMobileRow service={s} />}>
                                        <div className="flex flex-column gap-2 text-xs">
                                            <div className="flex align-items-center gap-2">
                                                <i className={`pi ${getCategoryIcon(s.category)} text-500 w-1rem`} aria-hidden="true" />
                                                <span className="text-500 font-medium w-6rem">Category</span>
                                                <span className="text-700">{s.category}</span>
                                            </div>
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-bolt text-500 w-1rem" aria-hidden="true" />
                                                <span className="text-500 font-medium w-6rem">Cost</span>
                                                <span className="text-primary font-semibold">
                                                    {s.credits_required || s.credits || 'N/A'} credits
                                                </span>
                                            </div>
                                        </div>
                                    </AccordionTab>
                                ))}
                            </Accordion>
                        </div>

                        {/* Desktop (≥ md) — DataTable */}
                        <div className="hidden md:block">
                            <DataTable
                                value={allServices}
                                dataKey="id"
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 20, 50]}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                                paginatorClassName="text-sm"
                                pageLinkSize={3}
                                responsiveLayout="scroll"
                                scrollable
                                stripedRows
                                showGridlines
                                size="small"
                                className="p-datatable-gridlines"
                                emptyMessage={
                                    <div className="flex flex-column align-items-center py-5">
                                        <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                        <span className="text-600">No services available</span>
                                    </div>
                                }
                            >
                                <Column
                                    field="category"
                                    header="Category"
                                    body={categoryBodyTemplate}
                                    sortable
                                    frozen
                                    alignFrozen="left"
                                    style={{ minWidth: '220px' }}
                                />
                                <Column
                                    field="name"
                                    header="Service Name"
                                    body={serviceNameBodyTemplate}
                                    sortable
                                    style={{ minWidth: '220px' }}
                                />
                                <Column
                                    field="credits_required"
                                    header="Credits Required"
                                    body={creditsBodyTemplate}
                                    sortable
                                    style={{ minWidth: '140px' }}
                                />
                            </DataTable>
                        </div>
                    </>
                )}
            </Card>

            {/* FAQ — reduced to 4 essentials */}
            <Card className="mb-3">
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-question-circle text-primary" aria-hidden="true" />
                    <h2 className="text-base font-medium text-900 m-0">Frequently Asked Questions</h2>
                </div>

                <Accordion>
                    <AccordionTab header={<span className="text-sm font-medium">What are credits and why do I need them?</span>}>
                        <p className="text-sm line-height-3 text-600 m-0">
                            Credits are the currency used to access verification services. Each service (credit report,
                            PAN verification, bank statement analysis, etc.) requires a specific number of credits per API
                            call. Maintain a credit balance to generate comprehensive reports for your borrowers.
                        </p>
                    </AccordionTab>

                    <AccordionTab header={<span className="text-sm font-medium">How do I recharge my wallet?</span>}>
                        <ol className="text-sm line-height-3 text-600 pl-4 mt-0 mb-3">
                            <li className="mb-2">Click the &quot;Recharge Wallet&quot; button at the top of this page</li>
                            <li className="mb-2">Select a recharge plan from the available options</li>
                            <li className="mb-2">Click &quot;Proceed to Payment&quot; — you&apos;ll be redirected to Razorpay</li>
                            <li className="mb-0">Credits are added immediately after successful payment</li>
                        </ol>
                        <Message severity="info" text="All recharges are non-refundable and credits do not expire." className="w-full" />
                    </AccordionTab>

                    <AccordionTab header={<span className="text-sm font-medium">What happens if I run out of credits?</span>}>
                        <p className="text-sm line-height-3 text-600 m-0">
                            If your balance reaches zero, you can&apos;t generate new reports or use verification services
                            until you recharge. Your existing reports and data remain accessible. We recommend keeping a
                            sufficient buffer to avoid interruptions.
                        </p>
                    </AccordionTab>

                    <AccordionTab header={<span className="text-sm font-medium">Are credits refundable? Do they expire?</span>}>
                        <p className="text-sm line-height-3 text-600 m-0">
                            <strong className="text-900">Refund policy:</strong> All credit purchases are non-refundable.
                            Choose your plan carefully based on your business needs.
                        </p>
                        <p className="text-sm line-height-3 text-600 m-0 mt-2">
                            <strong className="text-900">Expiration:</strong> Credits do not expire. Once purchased,
                            they remain in your wallet indefinitely until used.
                        </p>
                    </AccordionTab>
                </Accordion>
            </Card>

            {/* Recharge Dialog (P4 mobile-safe) */}
            <Dialog
                visible={rechargeDialog}
                onHide={() => {
                    setRechargeDialog(false);
                    setSelectedPlan(null);
                    setRechargeSubmitted(false);
                }}
                header="Recharge Credit Wallet"
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '900px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={() => {
                                setRechargeDialog(false);
                                setSelectedPlan(null);
                                setRechargeSubmitted(false);
                            }}
                            disabled={processingPayment}
                        />
                        <Button
                            label="Proceed to Payment"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            size="small"
                            onClick={initializePayment}
                            disabled={!selectedPlan || processingPayment}
                            loading={processingPayment}
                        />
                    </div>
                }
            >
                {rechargePlans.length === 0 ? (
                    <div className="flex flex-column align-items-center py-5">
                        <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                        <span className="text-600 text-sm mt-3">Loading recharge plans...</span>
                    </div>
                ) : (
                    <>
                        <h3 className="text-sm font-medium text-900 m-0 mb-3">Select a plan</h3>
                        <div className="grid">
                            {rechargePlans.map((plan) => (
                                <PlanCard
                                    key={plan.id}
                                    plan={plan}
                                    selected={selectedPlan?.id === plan.id}
                                    onSelect={setSelectedPlan}
                                />
                            ))}
                        </div>

                        {rechargeSubmitted && !selectedPlan && (
                            <small className="p-error block mt-2" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                Please select a recharge plan.
                            </small>
                        )}

                        {selectedPlan && (
                            <div className="surface-ground p-3 border-round mt-3 flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
                                <span className="text-700 text-sm font-medium">Selected plan:</span>
                                <span className="text-primary text-base font-semibold">
                                    {selectedPlan.credits} credits for {formatINR(selectedPlan.amount)}
                                </span>
                            </div>
                        )}

                        <Message
                            severity="info"
                            text="All recharges are non-refundable. Credits do not expire."
                            className="w-full mt-3"
                        />
                    </>
                )}
            </Dialog>
        </>
    );
};

export default CreditWallet;
